import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IServiceEmployment } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { getServiceEmploymentSettingsList } from '@frontend/components/pages/carePath/constants/serviceEmploymentSettingsList';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import type {
    IServiceEmploymentSettingList,
    ServiceEmploymentSettingType,
} from '../../addRights/utils/serviceEmploymentSettingsUtils';
import { getServiceEmploymentSettingsLabels } from '../../addRights/utils/serviceEmploymentSettingsUtils';
import { DateSettings } from '../DateSettings';
import { TurnRightOffLink } from '../TurnRightOffLink';
import { DeleteActivitiesModal } from './DeleteActivitiesModal';
import { ServiceEmploymentSettingList } from './ServiceEmploymentSettingList';

interface ServiceEmploymentSettingsProps {
    employmentId: string;
    episodeCategory: EpisodeCategory;
    organizationId: string;
    reloadServiceRight: () => Promise<void>;
    serviceRight: IServiceEmployment;
}

export type ServiceEmploymentSettingsMode = 'read' | 'edit';

export function ServiceEmploymentSettings(props: ServiceEmploymentSettingsProps) {
    const { organizationId, reloadServiceRight, serviceRight } = props;

    const [mode, setMode] = useState<ServiceEmploymentSettingsMode>('read');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [updatedServiceRight, setUpdatedServiceRight] = useState<IServiceEmployment>({ ...serviceRight });
    const [serviceEmploymentSettingsList, setServiceEmploymentSettingsList] = useState<IServiceEmploymentSettingList>(
        {}
    );
    const [deletedActivityList, setDeletedActivityList] = useState<Array<string>>([]);
    const [showDeleteActivitiesModal, setShowDeleteActivitiesModal] = useState<boolean>(false);

    const isEditAllowed = hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit);

    useEffect(() => {
        setServiceEmploymentSettingsList(getServiceEmploymentSettingsList(updatedServiceRight));
    }, [updatedServiceRight]);

    useEffect(() => {
        resetSettingsList();
        setMode('read');
    }, [serviceRight]);

    const header = useMemo(
        () =>
            isEditAllowed ? (
                <Card.Header
                    action={
                        mode === 'read' ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setMode('edit')}
                            />
                        ) : undefined
                    }
                    title={`${translateKey('addCarePathSectionTitles.serviceProviderSettings')} ${
                        serviceRight.service.name
                    }`}
                />
            ) : (
                <Card.Header
                    title={`${translateKey('addCarePathSectionTitles.serviceProviderSettings')} ${
                        serviceRight.service.name
                    }`}
                />
            ),
        [isEditAllowed, mode]
    );

    return (
        <Card>
            <Card.Transition type={mode} />
            {header}
            <Card.Body>
                <Form submitOnEnter={false}>
                    <DateSettings serviceRight={serviceRight} />
                    <ServiceEmploymentSettingList
                        mode={mode}
                        onChange={onChangeServiceEmploymentSettings}
                        serviceEmploymentSettingsList={serviceEmploymentSettingsList}
                        serviceRight={updatedServiceRight}
                    />
                </Form>
                {mode === 'edit' && (
                    <FormButtonRow>
                        <CancelButton
                            disabled={isSaving}
                            onCancel={async () => {
                                setMode('read');
                                resetSettingsList();
                            }}
                        />
                        <SaveButton
                            disabled={isSaving}
                            onSave={async () => {
                                if (deletedActivityList.length) {
                                    setShowDeleteActivitiesModal(true);
                                } else {
                                    handleSubmit();
                                }
                            }}
                        />
                    </FormButtonRow>
                )}

                <DeleteActivitiesModal
                    isVisible={showDeleteActivitiesModal}
                    onClose={() => setShowDeleteActivitiesModal(false)}
                    onConfirm={handleSubmit}
                />
            </Card.Body>
            {!serviceRight.endDate && isEditAllowed && (
                <Card.Footer>
                    <TurnRightOffLink
                        organizationId={organizationId}
                        reloadServiceRights={reloadServiceRight}
                        serviceRight={serviceRight}
                        serviceRightType={'ServiceEmployment'}
                    />
                </Card.Footer>
            )}
        </Card>
    );

    function onChangeServiceEmploymentSettings(newServiceRight: IServiceEmployment, deletedActivity?: string) {
        setUpdatedServiceRight({ ...newServiceRight });
        if (deletedActivity) {
            setDeletedActivityList((previousDeletedActivityList) => [...previousDeletedActivityList, deletedActivity]);
        }
    }

    async function handleSubmit() {
        if (mode === 'edit') {
            setShowDeleteActivitiesModal(false);
            setIsSaving(true);

            const serviceActivityAuthorizationsToDelete = generateServiceActivityAuthorizationsToDelete();
            const serviceEmploymentSettings = generateServiceEmploymentSettingsToUpdate();

            await fetchJson(`/api/serviceEmployments/${serviceRight.id}/updateActivitiesAndSettings`, 'POST', {
                serviceActivityAuthorizationsToDelete,
                serviceEmploymentSettings,
            });

            reloadServiceRight();
            setDeletedActivityList([]);
        }
        setMode('read');
        setIsSaving(false);
    }

    function generateServiceActivityAuthorizationsToDelete() {
        const activitiesToDelete = deletedActivityList.map((element) => element.replace('activity_', ''));

        return serviceRight.serviceActivityAuthorizations
            .filter((serviceActivityAuthorization) =>
                activitiesToDelete.includes(serviceActivityAuthorization.serviceActivity.id)
            )
            .map((serviceActivityAuthorization) => serviceActivityAuthorization.id);
    }

    function generateServiceEmploymentSettingsToUpdate() {
        let updatedSettingsList = { ...updatedServiceRight.settings };

        const serviceActivityCodes = updatedServiceRight.serviceActivityAuthorizations.map(
            (performer) => performer.serviceActivity.customControlCode
        );

        const settingNames = getServiceEmploymentSettingsLabels(serviceActivityCodes);

        for (const settingName of Object.keys(updatedSettingsList)) {
            if (!settingNames.includes(settingName as ServiceEmploymentSettingType)) {
                updatedSettingsList = {
                    ...updatedSettingsList,
                    [settingName]: null,
                };
            }
        }

        return updatedSettingsList;
    }

    function resetSettingsList() {
        setUpdatedServiceRight({ ...serviceRight });
        setServiceEmploymentSettingsList(getServiceEmploymentSettingsList(updatedServiceRight));
        setDeletedActivityList([]);
    }
}
